import { Fragment } from 'react';
import CodeBlock from '../generation/CodeBlock';

/**
 * Lightweight markdown renderer (no external deps): fenced code blocks,
 * inline code, bold, and paragraph/line-break handling — enough for chat.
 */
function renderInline(text, keyBase) {
  // Split on `inline code` and **bold**, keeping delimiters.
  const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g).filter(Boolean);
  return parts.map((part, i) => {
    const key = `${keyBase}-${i}`;
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={key} className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-[0.85em]">
          {part.slice(1, -1)}
        </code>
      );
    }
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={key} className="font-semibold text-content">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <Fragment key={key}>{part}</Fragment>;
  });
}

export default function Markdown({ content = '' }) {
  // Split into alternating text / fenced-code segments.
  const segments = content.split(/```/g);
  return (
    <div className="space-y-3 text-sm leading-relaxed text-content">
      {segments.map((seg, i) => {
        if (i % 2 === 1) {
          // Code block. First line may be a language hint.
          const nl = seg.indexOf('\n');
          const lang = nl > -1 ? seg.slice(0, nl).trim() : '';
          const code = nl > -1 ? seg.slice(nl + 1) : seg;
          return (
            <CodeBlock key={i} code={code.replace(/\n$/, '')} language={lang || 'text'} maxHeight="40vh" />
          );
        }
        if (!seg.trim()) return null;
        return (
          <div key={i} className="space-y-2">
            {seg.split(/\n{2,}/).map((para, p) => (
              <p key={p} className="whitespace-pre-wrap">
                {renderInline(para, `${i}-${p}`)}
              </p>
            ))}
          </div>
        );
      })}
    </div>
  );
}
